import type { PathWatcherEvent } from '@webcontainer/api';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { webcontainer as webcontainerPromise } from '~/lib/webcontainer';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { WORK_DIR } from '~/utils/constants';

/**
 * This isn't just a thin watch layer because if a file write happens (in an action) and watchPaths takes a tick or two
 * to trigger, you can call graph.wait() and there will be an empty queue because the paths didn't hit the watcher yet.
 *
 * So we track writes we make and confirm we have received watch events for them, to ensure that the module graph is up
 * to date when we do analysis.
 */

type Listener = (changes: PathWatcherEvent[]) => void | Promise<void>;

class AnalysisWatcher {
  #listeners: Listener[] = [];
  #pendingActionWrites = new Set();

  constructor() {
    this.init().catch(console.error);
  }

  addListener(listener: Listener) {
    this.#listeners.push(listener);
  }

  async init() {
    const webcontainer = await webcontainerPromise;

    webcontainer.internal.watchPaths(
      {
        include: [`${WORK_DIR}/**`],
        exclude: ['**/node_modules', '.git'],
        includeContent: true,
        ignoreHiddenFiles: true,
        ignoreHiddenFolders: true,
      },
      async (changes) => {
        for (const listener of this.#listeners) {
          await listener(changes);
        }

        for (const change of changes) {
          if (change.type === 'change') {
            this.#pendingActionWrites.delete(change.path);
          }
        }
      },
    );

    // in case the queue was pushed to before init() could complete
    this.#pendingActionWrites.clear();
  }

  /**
   * This method is called in the FileAction and applyAnnotations when a new file write begins.
   *
   * We don't track every file write, as we would have to match the excludes behaviour 1:1.
   *
   * Additionally, for analysis if we know the actions have definitely all been written we can be reasonably sure that the
   * events sent to the module graph are up to date.
   */
  queueAdd(path: string) {
    this.#pendingActionWrites.add(path);
  }

  /**
   * If the write was cancelled.
   */
  queueRemove(path: string) {
    this.#pendingActionWrites.delete(path);
  }

  async wait(attemptNumber: number = 0) {
    if (this.#pendingActionWrites.size === 0) {
      return;
    }

    /**
     * In the extremely rare case that the last file action has been reported as written but the watch change event has
     * not been triggered, we poll until we see the event has happened.
     *
     * If we've been waiting too long, log an error and continue anyway.
     *
     * This rarely happens, usually takes a frame or two, and almost certainly only loops once. The wait values are
     * extremely lenient.
     */

    if (attemptNumber > 150) {
      console.error('Watcher has not received all expected events');

      // this should never happen, so track an event
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.WatcherExpectedEvents,
          properties: {
            attemptNumber,
            actions: Array.from(this.#pendingActionWrites),
          },
        },
      });

      // cleanup failsafe
      this.#pendingActionWrites.clear();

      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
    await this.wait(attemptNumber + 1);
  }
}

export const analysisWatcher = new AnalysisWatcher();
